import { usePublicProjectReader, publicQueryKey, projectReaderGet } from '@/lib/public-project-reader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '@/lib/api';
import { useProjectStore } from '@/stores/project-store';
import type {
    Project,
    ProjectDetail,
    ProjectMember,
    ProjectInvitation,
    ProjectJoinLink,
    CreateProjectInput,
    UpdateProjectInput,
    CreateInvitationInput,
    ProjectRole,
    InviteInfo,
    JoinLinkInfo,
    ProjectDiscordWebhookStatus,
    ProjectTransferDocument,
    ProjectImportSummary,
} from '@/lib/schemas';

// ============================================
// QUERY KEYS
// ============================================

export const projectKeys = {
    all: ['projects'] as const,
    lists: () => [...projectKeys.all, 'list'] as const,
    list: (filters?: { archived?: boolean; role?: ProjectRole }) =>
        [...projectKeys.lists(), filters] as const,
    details: () => [...projectKeys.all, 'detail'] as const,
    detail: (id: string) => [...projectKeys.details(), id] as const,
    members: (projectId: string) => [...projectKeys.all, 'members', projectId] as const,
    invitations: (projectId: string) => [...projectKeys.all, 'invitations', projectId] as const,
    joinLink: (projectId: string) => [...projectKeys.all, 'join-link', projectId] as const,
    discordWebhook: (projectId: string) => [...projectKeys.all, 'discord-webhook', projectId] as const,
};

// ============================================
// PROJECTS HOOKS
// ============================================

export function useProjects(filters?: { archived?: boolean; role?: ProjectRole }) {
    const { setProjects, setLoading } = useProjectStore();

    return useQuery({
        queryKey: projectKeys.list(filters),
        queryFn: async () => {
            setLoading(true);
            const params = new URLSearchParams();
            if (filters?.archived !== undefined) {
                params.set('archived', String(filters.archived));
            }
            if (filters?.role) {
                params.set('role', filters.role);
            }
            const queryString = params.toString();
            const endpoint = queryString ? `/projects?${queryString}` : '/projects';
            const data = await apiGet<{ projects: Project[] }>(endpoint);
            setProjects(data.projects);
            return data.projects;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

export function useProject(projectId: string) {
    const { setCurrentProject, setLoading } = useProjectStore();

    return useQuery({
        queryKey: projectKeys.detail(projectId),
        queryFn: async () => {
            setLoading(true);
            const data = await apiGet<ProjectDetail>(`/projects/${projectId}`);
            setCurrentProject(data);
            return data;
        },
        enabled: !!projectId,
    });
}

export function useCreateProject() {
    const queryClient = useQueryClient();
    const { addProject } = useProjectStore();

    return useMutation({
        mutationFn: async (data: CreateProjectInput) => {
            return apiPost<Project>('/projects', data);
        },
        onSuccess: (project) => {
            addProject(project);
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}

export function useUpdateProject(projectId: string) {
    const queryClient = useQueryClient();
    const { updateProject } = useProjectStore();

    return useMutation({
        mutationFn: async (data: UpdateProjectInput) => {
            return apiPatch<Project>(`/projects/${projectId}`, data);
        },
        onSuccess: (project) => {
            updateProject(projectId, project);
            queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}

export function useDeleteProject() {
    const queryClient = useQueryClient();
    const { removeProject } = useProjectStore();

    return useMutation({
        mutationFn: async (projectId: string) => {
            return apiDelete<{ message: string }>(`/projects/${projectId}`);
        },
        onSuccess: (_, projectId) => {
            removeProject(projectId);
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}

// ============================================
// PROJECT MEMBERS HOOKS
// ============================================

export function useProjectMembers(projectId: string) {
    const reader = usePublicProjectReader();
    return useQuery({
        queryKey: publicQueryKey(reader, projectKeys.members(projectId)),
        queryFn: async () => {
            const data = await projectReaderGet<{ members: ProjectMember[] }>(reader, 
                `/projects/${projectId}/members`
            );
            return data.members;
        },
        refetchInterval: reader ? 10000 : false,
        enabled: !!projectId,
    });
}

export function useAddMember(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ userId, role }: { userId: string; role?: ProjectRole }) => {
            return apiPost<ProjectMember>(`/projects/${projectId}/members`, {
                userId,
                role,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}

export function useUpdateMemberRole(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ memberId, role }: { memberId: string; role: ProjectRole }) => {
            return apiPatch<ProjectMember>(`/projects/${projectId}/members/${memberId}`, {
                role,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
        },
    });
}

export function useRemoveMember(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (memberId: string) => {
            return apiDelete<{ message: string }>(
                `/projects/${projectId}/members/${memberId}`
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}

// ============================================
// PROJECT INVITATIONS HOOKS
// ============================================

export function useProjectInvitations(projectId: string) {
    return useQuery({
        queryKey: projectKeys.invitations(projectId),
        queryFn: async () => {
            const data = await apiGet<{ invitations: ProjectInvitation[] }>(
                `/projects/${projectId}/invites`
            );
            return data.invitations;
        },
        enabled: !!projectId,
    });
}

export function useCreateInvitation(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateInvitationInput) => {
            return apiPost<ProjectInvitation>(`/projects/${projectId}/invites`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: projectKeys.invitations(projectId),
            });
        },
    });
}

export function useCancelInvitation(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (inviteId: string) => {
            return apiDelete<{ message: string }>(
                `/projects/${projectId}/invites/${inviteId}`
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: projectKeys.invitations(projectId),
            });
        },
    });
}

export function useProjectJoinLink(projectId: string) {
    return useQuery({
        queryKey: projectKeys.joinLink(projectId),
        queryFn: async () => {
            const data = await apiGet<{ joinLink: ProjectJoinLink | null }>(
                `/projects/${projectId}/join-link`
            );
            return data.joinLink;
        },
        enabled: !!projectId,
    });
}

export function useGenerateProjectJoinLink(projectId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => apiPost<ProjectJoinLink>(`/projects/${projectId}/join-link`),
        onSuccess: (joinLink) => {
            queryClient.setQueryData(projectKeys.joinLink(projectId), joinLink);
        },
    });
}

export function useDisableProjectJoinLink(projectId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => apiDelete<{ message: string }>(`/projects/${projectId}/join-link`),
        onSuccess: () => {
            queryClient.setQueryData(projectKeys.joinLink(projectId), null);
        },
    });
}

export function useProjectDiscordWebhook(projectId: string) {
    return useQuery({
        queryKey: projectKeys.discordWebhook(projectId),
        queryFn: () => apiGet<ProjectDiscordWebhookStatus>(`/projects/${projectId}/discord-webhook`),
        enabled: !!projectId,
    });
}

export function useSaveProjectDiscordWebhook(projectId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (webhookUrl: string) => apiPut<ProjectDiscordWebhookStatus, { webhookUrl: string }>(
            `/projects/${projectId}/discord-webhook`,
            { webhookUrl }
        ),
        onSuccess: (status) => queryClient.setQueryData(projectKeys.discordWebhook(projectId), status),
    });
}

export function useTestProjectDiscordWebhook(projectId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => apiPost<{ success: boolean }>(`/projects/${projectId}/discord-webhook/test`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.discordWebhook(projectId) }),
    });
}

export function useDeleteProjectDiscordWebhook(projectId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => apiDelete<{ success: boolean }>(`/projects/${projectId}/discord-webhook`),
        onSuccess: () => queryClient.setQueryData(projectKeys.discordWebhook(projectId), {
            configured: false,
            enabled: false,
            webhookName: null,
            channelId: null,
            guildId: null,
            lastSuccessAt: null,
            lastError: null,
        } satisfies ProjectDiscordWebhookStatus),
    });
}

export function useExportProjectTasks(projectId: string) {
    return useMutation({
        mutationFn: () => apiGet<ProjectTransferDocument>(`/projects/${projectId}/task-export`),
    });
}

export function usePreviewProjectTaskImport(projectId: string) {
    return useMutation({
        mutationFn: (document: ProjectTransferDocument) => apiPost<ProjectImportSummary, ProjectTransferDocument>(
            `/projects/${projectId}/task-import/preview`,
            document
        ),
    });
}

export function useImportProjectTasks(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (document: ProjectTransferDocument) => apiPost<ProjectImportSummary, ProjectTransferDocument>(
            `/projects/${projectId}/task-import`,
            document
        ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks', 'list', projectId] });
            queryClient.invalidateQueries({ queryKey: ['tasks', 'my'] });
            queryClient.invalidateQueries({ queryKey: ['customFields', 'list', projectId] });
        },
    });
}

// ============================================
// PUBLIC INVITATION HOOKS
// ============================================

export function useInviteInfo(token: string) {
    return useQuery({
        queryKey: ['invite', token],
        queryFn: async () => {
            return apiGet<InviteInfo>(`/invites/${token}`);
        },
        enabled: !!token,
        retry: false,
    });
}

export function useAcceptInvite() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (token: string) => {
            return apiPost<{ projectId: string; memberId: string; role: ProjectRole }>(
                `/invites/${token}/accept`
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
        },
    });
}

export function useJoinLinkInfo(token: string) {
    return useQuery({
        queryKey: ['join-link', token],
        queryFn: async () => apiGet<JoinLinkInfo>(`/join-links/${token}`),
        enabled: !!token,
        retry: false,
    });
}

export function useAcceptJoinLink() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (token: string) => apiPost<{
            projectId: string;
            memberId: string | null;
            role: ProjectRole;
            alreadyMember: boolean;
        }>(`/join-links/${token}/accept`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
            queryClient.invalidateQueries({ queryKey: ['projects', 'favorites'] });
        },
    });
}

// ============================================
// FAVORITE PROJECTS HOOKS
// ============================================

export function useFavoriteProjects() {
    return useQuery({
        queryKey: ['projects', 'favorites'],
        queryFn: async () => {
            const data = await apiGet<{ projects: Project[] }>('/projects/favorites');
            return data.projects;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

export function useToggleFavorite() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (projectId: string) => {
            return apiPost<{ isFavorite: boolean }>(`/projects/${projectId}/favorite`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
            queryClient.invalidateQueries({ queryKey: ['projects', 'favorites'] });
        },
    });
}

// ============================================
// LEAVE PROJECT HOOK
// ============================================

export function useLeaveProject() {
    const queryClient = useQueryClient();
    const { removeProject } = useProjectStore();

    return useMutation({
        mutationFn: async ({ projectId, memberId }: { projectId: string; memberId: string }) => {
            return apiDelete<{ message: string }>(
                `/projects/${projectId}/members/${memberId}`
            );
        },
        onSuccess: (_, { projectId }) => {
            removeProject(projectId);
            queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
            queryClient.invalidateQueries({ queryKey: ['projects', 'favorites'] });
        },
    });
}
